import React, { Fragment, useState, useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { connect, useSelector } from 'react-redux';

import { AiAssistChat } from 'components/AiAssist/Chat/AiAssistChat';
import ChatDrawer from 'components/AiAssist/Chat/ChatDrawer';
import { expStatus } from 'components/Core/NonPayEnabled/UnpayableCard/Growth/consts/consts';
import { useNonPayEnableCookie } from 'components/Core/NonPayEnabled/UnpayableCard/Growth/hooks/useNonPayEnableCookie';
import ExtendBtn from 'components/Shared/ExtendBtn/ExtendBtn';
import { FeedbackModal } from 'components/Shared/Feedback/FeedbackModal/FeedbackModal';
import SendMerchantMessageModal from 'components/Shared/SendMerchantMessage/SendMerchantMessageModal';
import { useAiAssistChatMessages } from 'hooks/useAiAssistChatMessages';
import SegmentIO from 'reporting/SegmentIO';
import { SplunkReporter } from 'reporting/splunk/SplunkReporter';
import { companyInfoSelectors } from 'store/companyInfo/selectors';
import { sendNpeFreeTextEmailEvent } from 'store/insight/slice';
import { saleSelectors } from 'store/sale/selectors';
import { RootState } from 'store/store';
import { colors } from 'styles/cp';
import { ShowModalFunc } from 'types/Modal';

const logger = 'components/AiAssist/FAQ/MoreHelpFaq';
const splunkReporter = SplunkReporter.getInstance();

interface MoreHelpFaqProps {
  showModal: ShowModalFunc;
  sendNpeFreeTextEmailEvent: (payload?: {
    merchantEmail: string;
    senderName: string;
    senderContactInfo: string;
    freeTextMessage: string;
  }) => void;
  freeTextEmailOINPEventStatus?: string;
  sale: any;
  payment: any;
  portal: string;
  env: string;
  wallet: any;
  realmId: string;
  ssrtid: string;
  authToken: string;
  insight: any;
}

const _MoreHelpFaq: React.FC<MoreHelpFaqProps> = ({
  freeTextEmailOINPEventStatus,
  sale,
  payment,
  portal,
  env,
  wallet,
  realmId,
  ssrtid,
  authToken,
  insight,
}) => {
  const intl = useIntl();
  const [isAnswerOpen, setIsAnswerOpen] = useState(true);
  const [showFeedbackSurvey, setShowFeedbackSurvey] = useState(false);
  const [isChatDrawerOpen, setIsChatDrawerOpen] = useState(false);
  const [nonPayEnableStatus, setNonPayEnableStatus] = useState<expStatus>();
  const { submitChatMessages } = useAiAssistChatMessages();
  const domainId = useSelector((state: RootState) => state.insight.domainId) || sale?.id;
  const companyInfo = useSelector((state: RootState) => state.companyInfo);
  const companyName = companyInfoSelectors.nameSelector(companyInfo);
  const merchantEmail = companyInfoSelectors.emailSelector(companyInfo);
  const customerEmail = useSelector(
    (state: RootState) => saleSelectors.toEmailsSelector(state.sale)?.[0]
  );
  const messages = useSelector((state: RootState) => state.chat.messages);

  const { getNpeCookie, setNpeCookie } = useNonPayEnableCookie({
    domainId,
    nonPayEnableStatus,
    setNonPayEnableStatus,
  });

  const handleRowClick = () => {
    setIsAnswerOpen(!isAnswerOpen);
  };

  const onOpenChatClick = () => {
    setIsChatDrawerOpen(true);

    SegmentIO.aiAssistChatOpened({
      domain_id: domainId,
    });
  };

  useEffect(() => {
    if (freeTextEmailOINPEventStatus === 'SENT_SUCCESSFULLY') {
      setNpeCookie({
        ...getNpeCookie(),
        selection: expStatus.FREE_TEXT_EMAIL_SENT,
        isFirstVisit: true,
      });
    } else if (freeTextEmailOINPEventStatus === 'ERROR') {
      setNpeCookie({
        ...getNpeCookie(),
        selection: expStatus.RETURNED_ERROR,
        isFirstVisit: true,
      });
    }
  }, [freeTextEmailOINPEventStatus, setNpeCookie, getNpeCookie]);

  const handleCloseChat = () => {
    // Only execute if messages array is not empty
    if (messages?.length === 0) {
      setIsChatDrawerOpen(false);
      return;
    }
    // Log chat close when user manually closes the drawer
    splunkReporter.contextual({
      logInfo: { logLevel: 'info', logger },
      event: 'report',
      action: 'render',
      activityInfo: {
        feature: 'aiAssist',
        action: 'chatClosed',
        messageCount: messages?.length,
        messages,
        closeMethod: 'manual',
      },
    });

    setIsChatDrawerOpen(false);
    submitChatMessages({
      messages,
      sale,
      payment,
      portal,
      env,
      wallet,
      realmId,
      ssrtid,
      authToken,
      insight,
      activityObject: 'MoreHelpFaq',
      logger,
    });
  };

  const handleSendMessageModalOpen =
    (open: () => void) => (e: React.MouseEvent<HTMLAnchorElement>) => {
      e.preventDefault();
      open();
    };

  return (
    <Fragment>
      <div className="faq-container" onClick={handleRowClick}>
        <ChatDrawer
          onClose={handleCloseChat}
          isOpen={isChatDrawerOpen}
          content={isChatDrawerOpen ? <AiAssistChat /> : null}
        />

        <div className="question">
          <FormattedMessage id="FAQ_MORE_HELP" defaultMessage="Need more help?" />
        </div>
        <ExtendBtn
          opened={isAnswerOpen}
          handler={handleRowClick}
          ariaLabel={intl.formatMessage({
            id: isAnswerOpen ? 'FAQ_MORE_HELP_HIDE' : 'FAQ_MORE_HELP_SHOW',
            defaultMessage: isAnswerOpen ? 'Hide help options' : 'Show help options',
          })}
        />
      </div>
      {isAnswerOpen && (
        <div className="answer-container">
          <a
            className="modal-link"
            href={'#'}
            onClick={() => {
              setShowFeedbackSurvey(true);
            }}
          >
            Submit feedback
          </a>
          {showFeedbackSurvey && (
            <FeedbackModal
              onHide={() => {
                setShowFeedbackSurvey(false);
              }}
            />
          )}
          <span
            style={{
              marginTop: 10,
              display: 'block',
            }}
          >
            <SendMerchantMessageModal
              companyName={companyName ?? ''}
              merchantEmail={merchantEmail || ''}
              customerEmail={customerEmail ?? ''}
              trigger={(open) => (
                <a href="#" onClick={handleSendMessageModalOpen(open)} className="modal-link">
                  Send a message to the business
                </a>
              )}
            />
          </span>
          <span
            style={{
              marginTop: 10,
              display: 'block',
            }}
          >
            <a href="#" onClick={onOpenChatClick} className={'modal-link'}>
              <FormattedMessage id={'open_feedback_chat'} defaultMessage={'Open chat'} />
            </a>
          </span>
        </div>
      )}
      <style jsx>{`
        .faq-container {
          padding: 10px 18px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          cursor: pointer;
          .question {
            font-family: AvenirNextforINTUIT-Medium;
            padding: 8px 0 0 0;
            flex-grow: 1;
          }
        }
        .answer-container {
          padding: 0 18px 10px;
          max-height: ${isAnswerOpen ? '250px' : 0};
          transition: all 0.35s ease-in-out;
          overflow: hidden;
          :global(svg) {
            transition: all 0.15s linear;
          }
          :global(.answer) {
            padding-bottom: 10px;
            display: flex;
            color: ${colors.gray01};
            font-size: 14px;
          }
        }
        :global(.question-text) {
          font-size: 14px;
          font-family: AvenirNextforINTUIT-Regular;
        }
        :global(.answer-text) {
          text-decoration: none;
          bold: off;
        }
        .modal-link {
          color: ${colors.blue};
          text-decoration: none;
          padding: 2px;
          font-family: AvenirNextforINTUIT-Medium;
          :hover {
            text-decoration: underline;
          }
        }
      `}</style>
    </Fragment>
  );
};

function mapStateToProps(store: RootState) {
  const {
    config: { ssrtid, portal, env },
    insight,
    auth: { authToken, realmId },
    sale,
    wallet,
    payment,
  } = store;
  return {
    showFeedbackForm: true,
    freeTextEmailOINPEventStatus: insight.freeTextEmailOINPEventStatus,
    sale,
    payment,
    portal,
    env,
    wallet,
    realmId,
    ssrtid,
    authToken,
    insight,
  };
}

export default connect(mapStateToProps, {
  sendNpeFreeTextEmailEvent,
})(_MoreHelpFaq);
